import {useState, useEffect} from 'react';
import {motion, AnimatePresence} from 'framer-motion';

import {useNavigate, useUrl} from '@shopify/hydrogen';

import {StandardInput, IconRadios} from './FormInputs.client';

import {NextDivButton, NextButton} from './DripSubscribeForm.client';
import PortableText from '../portableText/PortableText.client';

const QuizStep = ({
  nextStep,
  handleChange,
  step: {stepText, field},
  isFinal,
  canContinue,
  snakedName,
  handleKeyDown,
  loading,
  activeStep,
  prevStep,
  nextText,
  backgroundColor,
}: any) => {
  const stepField = field[0] || {};

  return (
    <motion.div
      className="col-start-2 col-end-14 self-center text-center md:col-start-3 md:col-end-13 lg:col-start-4 lg:col-end-12"
      initial={{opacity: 0, y: 10}}
      animate={{opacity: 1, y: 0}}
      exit={{opacity: 0, y: 10}}
    >
      <PortableText blocks={stepText} />
      {stepField._type === 'formElement' ? (
        <div className="mt-15 grid grid-cols-2">
          <StandardInput
            type={stepField.inputType}
            placeholder={stepField.label}
            required={stepField.required}
            className="col-start-1 col-end-3 row-start-1 row-end-1"
            handleChange={handleChange}
            name={snakedName}
            onKeyDown={handleKeyDown}
            backgroundColor={backgroundColor}
          />
          {isFinal ? (
            <>
              {!loading ? (
                <NextButton
                  className="col-start-2 col-end-2 row-start-1 row-end-1 self-center justify-self-end"
                  type="submit"
                  disabled={!canContinue}
                />
              ) : (
                <div className="col-start-2 col-end-2 row-start-1 row-end-1 self-center justify-self-end text-brand">
                  <svg
                    className="-ml-1 mr-3 h-5 w-5 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
              )}
            </>
          ) : (
            canContinue && (
              <NextDivButton
                className="col-start-2 col-end-2 row-start-1 row-end-1 self-center justify-self-end"
                onClick={nextStep}
              />
            )
          )}
        </div>
      ) : (
        stepField._type === 'iconRadios' && (
          <div>
            <IconRadios
              {...stepField}
              nextStep={nextStep}
              handleChange={handleChange}
              fieldName={snakedName}
            />
          </div>
        )
      )}
      <nav className="flex justify-center pt-6 text-lg font-semibold md:hidden md:justify-between">
        {/* Back */}
        {activeStep > 0 && !isFinal ? (
          <div className="flex cursor-pointer items-center" onClick={prevStep}>
            <div className="mr-2 w-4">
              <svg
                id="Layer_1"
                data-name="Layer 1"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 26 22"
                className="fill-current"
              >
                <path
                  d="M10.29.29l-10,10-.08.1-.06.09-.06.1,0,.11,0,.11,0,.12v.23l0,.12,0,.1,0,.11.08.12.09.11,10,10,.1.08a1,1,0,0,0,1.32-.08l.08-.1a1,1,0,0,0-.08-1.32L3.41,12H25.12A1,1,0,0,0,25,10H3.41l8.3-8.29.08-.1A1,1,0,0,0,11.71.29,1,1,0,0,0,10.29.29Z"
                  transform="translate(0 0)"
                />
              </svg>
            </div>
            Back
          </div>
        ) : (
          <div className="h-7" />
        )}
        {/* Next */}
        {canContinue && !isFinal && (
          <div className="flex cursor-pointer items-center" onClick={nextStep}>
            {nextText}
            <div className="ml-2 w-4">
              <svg
                id="Layer_1"
                data-name="Layer 1"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 26 22"
                className="fill-current"
              >
                <path
                  d="M14.29.29a1,1,0,0,0-.08,1.32l.08.1L22.59,10H1a1,1,0,0,0-.12,2H22.59l-8.3,8.29a1,1,0,0,0-.08,1.32l.08.1a1,1,0,0,0,1.32.08l.1-.08,10-10,.09-.11.08-.12.05-.11,0-.1,0-.12v-.23l0-.12,0-.11,0-.11-.06-.1-.06-.09-.08-.1-10-10A1,1,0,0,0,14.29.29Z"
                  transform="translate(0 0)"
                />
              </svg>
            </div>
          </div>
        )}
        {/* Final Submit */}
        {canContinue && isFinal && (
          <button className="flex items-center font-semibold" type="submit">
            Submit
            <div className="ml-2 w-4">
              <svg
                id="Layer_1"
                data-name="Layer 1"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 26 22"
                className="fill-current"
              >
                <path
                  d="M14.29.29a1,1,0,0,0-.08,1.32l.08.1L22.59,10H1a1,1,0,0,0-.12,2H22.59l-8.3,8.29a1,1,0,0,0-.08,1.32l.08.1a1,1,0,0,0,1.32.08l.1-.08,10-10,.09-.11.08-.12.05-.11,0-.1,0-.12v-.23l0-.12,0-.11,0-.11-.06-.1-.06-.09-.08-.1-10-10A1,1,0,0,0,14.29.29Z"
                  transform="translate(0 0)"
                />
              </svg>
            </div>
          </button>
        )}
      </nav>
    </motion.div>
  );
};

export const QuizForm = ({
  steps,
  subscriberEmail,
  successMessage,
  onClose,
  backgroundColor,
}: any) => {
  const [step, setStep] = useState<any>(0);
  const [values, setValues] = useState<any>({});
  const [success, setSuccess] = useState<any>(false);
  const [loading, setLoading] = useState<any>(false);
  const location = useUrl();
  const navigate = useNavigate();

  const host = import.meta.env.PUBLIC_FUNCTION_HOST;

  const activeStep = steps[step];

  const stepField = (activeStep && activeStep.field[0]) || {};

  const {fieldName, required} = stepField;

  const snakedName =
    (fieldName && fieldName.current && fieldName.current.replace(/-/g, '_')) ||
    null;

  const [canContinue, setCanContinue] = useState(
    !required || (required && values[snakedName]),
  );

  const isFinal = step + 1 === steps.length;

  useEffect(() => {
    setCanContinue(!required || (required && values[snakedName]));
  }, [values, fieldName, snakedName, required]);

  const nextStep = () => {
    if (step + 1 < steps.length) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleChange = (event: any) => {
    event.persist();
    setValues((previousValues: any) => ({
      ...previousValues,
      [event.target.name]: event.target.value,
    }));
  };

  const handleKeyDown = (event: any) => {
    if (event.keyCode === 13 && canContinue && !isFinal) {
      event.preventDefault();
    }
  };

  const handleQuizSubmit = async (event: any) => {
    event.preventDefault();
    setLoading(true);

    // Hit the serverless function
    await fetch(`${host}/submit-drip-quiz`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({email: subscriberEmail, values: {...values}}),
    });

    setSuccess(true);
  };

  useEffect(() => {
    navigate(
      `${location.pathname}?form_step=${success ? 'completed' : step + 1}`,
    );
    return () => {
      navigate(`${location.pathname}`);
    };
  }, [step, success]);

  return (
    <motion.form
      onSubmit={handleQuizSubmit}
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      exit={{opacity: 0}}
      className="grid h-screen grid-cols-14 grid-rows-1"
      handleKeyDown={handleKeyDown}
    >
      <AnimatePresence mode="wait">
        {success ? (
          <motion.div
            key="successMessage"
            className="col-start-2 col-end-14 self-center text-center md:col-start-4 md:col-end-12"
            initial={{opacity: 0, y: 10}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, y: 10}}
          >
            <PortableText blocks={successMessage} />
            <div className="btn-standard mx-auto mt-10" onClick={onClose}>
              Close
            </div>
          </motion.div>
        ) : (
          <QuizStep
            activeStep={step}
            step={steps[step]}
            key={step}
            nextStep={nextStep}
            prevStep={prevStep}
            handleChange={handleChange}
            isFinal={isFinal}
            canContinue={canContinue}
            snakedName={snakedName}
            handleKeyDown={handleKeyDown}
            loading={loading}
            nextText={!required && !values[snakedName] ? 'Skip' : 'Next'}
            backgroundColor={backgroundColor}
          />
        )}
      </AnimatePresence>
      <nav className="col-start-2 col-end-14 hidden justify-between py-6 text-lg font-semibold md:flex">
        {/* Back */}
        {step > 0 && !isFinal ? (
          <div className="flex cursor-pointer items-center" onClick={prevStep}>
            <div className="mr-2 w-4">
              <svg
                id="Layer_1"
                data-name="Layer 1"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 26 22"
                className="fill-current"
              >
                <path
                  d="M10.29.29l-10,10-.08.1-.06.09-.06.1,0,.11,0,.11,0,.12v.23l0,.12,0,.1,0,.11.08.12.09.11,10,10,.1.08a1,1,0,0,0,1.32-.08l.08-.1a1,1,0,0,0-.08-1.32L3.41,12H25.12A1,1,0,0,0,25,10H3.41l8.3-8.29.08-.1A1,1,0,0,0,11.71.29,1,1,0,0,0,10.29.29Z"
                  transform="translate(0 0)"
                />
              </svg>
            </div>
            Back
          </div>
        ) : (
          <div />
        )}
        {/* Next */}
        {canContinue && !isFinal && (
          <div className="flex cursor-pointer items-center" onClick={nextStep}>
            {!required && !values[snakedName] ? 'Skip' : 'Next'}
            <div className="ml-2 w-4">
              <svg
                id="Layer_1"
                data-name="Layer 1"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 26 22"
                className="fill-current"
              >
                <path
                  d="M14.29.29a1,1,0,0,0-.08,1.32l.08.1L22.59,10H1a1,1,0,0,0-.12,2H22.59l-8.3,8.29a1,1,0,0,0-.08,1.32l.08.1a1,1,0,0,0,1.32.08l.1-.08,10-10,.09-.11.08-.12.05-.11,0-.1,0-.12v-.23l0-.12,0-.11,0-.11-.06-.1-.06-.09-.08-.1-10-10A1,1,0,0,0,14.29.29Z"
                  transform="translate(0 0)"
                />
              </svg>
            </div>
          </div>
        )}
        {/* Final Submit */}
        {canContinue && isFinal && !success && (
          <button
            className="richH5 flex items-center font-bold text-dark-grey"
            type="submit"
          >
            Submit
            <div className="ml-2 w-4">
              <svg
                id="Layer_1"
                data-name="Layer 1"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 26 22"
                className="fill-current"
              >
                <path
                  d="M14.29.29a1,1,0,0,0-.08,1.32l.08.1L22.59,10H1a1,1,0,0,0-.12,2H22.59l-8.3,8.29a1,1,0,0,0-.08,1.32l.08.1a1,1,0,0,0,1.32.08l.1-.08,10-10,.09-.11.08-.12.05-.11,0-.1,0-.12v-.23l0-.12,0-.11,0-.11-.06-.1-.06-.09-.08-.1-10-10A1,1,0,0,0,14.29.29Z"
                  transform="translate(0 0)"
                  fill="#414242"
                />
              </svg>
            </div>
          </button>
        )}
      </nav>
    </motion.form>
  );
};
